/**
 * 14030 — Helpers shared by the Questionnaire & Risk Analysis tab.
 *
 * NOTHING in here is hardcoded — every threshold / rule / palette comes from
 * the runtime config returned by `/RiskScoring/methodology` (`config` field).
 * Callers pass the config in; helpers fall back to a permissive default
 * (mirroring Airtable's published rules) when no config is provided, so
 * unrelated pages that haven't loaded the methodology yet still render.
 */

const DEFAULT_CONFIG = Object.freeze({
  answer_factors: [
    { label: "Yes",     factor: 0.0, findingType: "Compliant" },
    { label: "No",      factor: 1.0, findingType: "NonCompliant" },
    { label: "NA",      factor: 0.0, findingType: "NotApplicable" },
    { label: "Partial", factor: 1.0, findingType: "PartiallyCompliant" },
    { label: "Unknown", factor: 0.0, findingType: "NotScored" },
  ],
  question_rag_bands: [
    { band: "Red",    min: 50, max: 125 },
    { band: "Yellow", min: 20, max: 49 },
    { band: "Green",  min: 0,  max: 19 },
  ],
  question_criticality_rules: [
    { tier: "Critical", risk_score_min: 50, or_impact_min: 4, color: "red" },
    { tier: "Major",    risk_score_min: 20, risk_score_max: 49, or_impact_eq: 3, color: "orange" },
    { tier: "Minor",    color: "gray" },
  ],
  // 14030 spec: severity tier on a non-Compliant finding follows the QRS band —
  // ≥50 Critical, 35-49 High, 20-34 Medium, <20 Low.
  severity_tier_bins: [
    { tier: "Critical", min: 50 },
    { tier: "High",     min: 35 },
    { tier: "Medium",   min: 20 },
    { tier: "Low",      min: 0 },
  ],
  vqq_rollup: "RiskWeightedCompliance",
  section_rollup: "MeanPlusHalfStDev",
  // 14030 spec: VQQ-level Risk Score % — Green ≥80, Yellow 50-79, Red <50.
  vqq_rag_bands: [
    { band: "Green",  min: 80, max: 100 },
    { band: "Yellow", min: 50, max: 79 },
    { band: "Red",    min: 0,  max: 49 },
  ],
  // 14030 spec: intrinsic Section Risk Score (1-125) uses the per-question band
  // thresholds.
  section_risk_rag_bands: [
    { band: "Red",    min: 50, max: 125 },
    { band: "Yellow", min: 20, max: 49 },
    { band: "Green",  min: 0,  max: 19 },
  ],
  // 14030 spec: Critical=5, Major=3, Minor=1.
  question_criticality_numeric_map: [
    { tier: "Critical", weight: 5 },
    { tier: "Major",    weight: 3 },
    { tier: "Minor",    weight: 1 },
  ],
  // 14030 spec: ≥4 Critical, >2 & <4 Major, ≤2 Minor.
  section_criticality_bands: [
    { tier: "Critical", min_mean: 4.0, max_mean: null, inclusive_min: true,  inclusive_max: true,  color: "red",    priority: 1 },
    { tier: "Major",    min_mean: 2.0, max_mean: 4.0,  inclusive_min: false, inclusive_max: false, color: "orange", priority: 2 },
    { tier: "Minor",    min_mean: 0.0, max_mean: 2.0,  inclusive_min: true,  inclusive_max: true,  color: "gray",   priority: 3 },
  ],
  standard_palette: [],
});

/**
 * Pull the parsed config out of whatever the methodology endpoint returned.
 * Accepts the controller's `{ ..., config }` envelope, a bare config object,
 * or null/undefined. Always returns something usable.
 */
export function resolveConfig(methodology) {
  if (!methodology) return DEFAULT_CONFIG;
  if (methodology.config && typeof methodology.config === "object") {
    return { ...DEFAULT_CONFIG, ...methodology.config };
  }
  if (Array.isArray(methodology.answer_factors)) {
    return { ...DEFAULT_CONFIG, ...methodology };
  }
  return DEFAULT_CONFIG;
}

/**
 * Walks the criticality rules in the order they appear in the config. First
 * rule whose constraints all match wins. Each rule can constrain
 * Risk Score (min/max inclusive) and/or Impact (min OR equality). A rule
 * with no constraints acts as the catch-all "else" branch.
 */
export function computeCriticality(riskTotal, impact = null, config = null) {
  if (riskTotal == null) return null;
  const cfg = resolveConfig(config);
  const i = Number(impact);
  const hasImpact = Number.isFinite(i);

  for (const rule of cfg.question_criticality_rules) {
    const rsMin = rule.risk_score_min;
    const rsMax = rule.risk_score_max;
    const orImpactMin = rule.or_impact_min;
    const orImpactEq = rule.or_impact_eq;

    const noConstraints = rsMin == null && rsMax == null && orImpactMin == null && orImpactEq == null;
    if (noConstraints) return rule.tier;

    const riskOk = (rsMin == null || riskTotal >= rsMin) && (rsMax == null || riskTotal <= rsMax);
    const impactOk =
      (orImpactMin != null && hasImpact && i >= orImpactMin) ||
      (orImpactEq != null && hasImpact && i === orImpactEq);

    // A rule's risk-score window OR an Impact promotion both qualify it.
    // Risk-window match requires the question to land in [rsMin, rsMax];
    // Impact match alone qualifies even when the Risk Score is below.
    const ruleHasRiskWindow = rsMin != null || rsMax != null;
    if (ruleHasRiskWindow && riskOk) return rule.tier;
    if (impactOk) return rule.tier;
  }
  return null;
}

/**
 * 14030 spec realized-risk classifier. A finding contributes its FULL Question
 * Risk Score (I×L×D) to the realized-risk numerator iff its findingType is
 * non-Compliant (NonCompliant or PartiallyCompliant). Compliant / NA findings
 * contribute 0. NotScored / Undetermined / OutOfScope are excluded from BOTH
 * the numerator and denominator.
 */
function isNonCompliant(findingType) {
  const t = String(findingType || "").toLowerCase();
  return t === "noncompliant" || t === "partiallycompliant";
}

function isNotScored(findingType) {
  const t = String(findingType || "").toLowerCase();
  return t === "notscored" || t === "undetermined" || t === "outofscope" || t === "";
}

/**
 * Aggregates a list of scored rows into a section-level summary.
 *   - score (vendor-driven %) = (1 − Σ realized_risk / Σ total_intrinsic_risk) × 100
 *     where realized_risk = full QRS on non-Compliant, 0 on Compliant,
 *     excluded if Not Scored. Mirrors BE spec verbatim (14030 AC #12).
 *   - rag = mapped via config.vqq_rag_bands (Green ≥80, Yellow 50-79, Red <50).
 *   - intrinsic_section_risk_score = Mean(QRS) + 0.5 × sample StDev (n-1).
 *   - intrinsic_section_risk_rag = banded ≥50 Red / 20-49 Yellow / <20 Green.
 *   - criticality = bucketed mean of Question Criticality Numeric values
 *     {Critical=5, Major=3, Minor=1}; bands ≥4 Critical / >2 & <4 Major / ≤2 Minor.
 */
export function aggregateSection(scoredRows, config = null) {
  const cfg = resolveConfig(config);
  const out = {
    total: 0,
    scored: 0,
    compliant: 0,
    rag: null,
    score: null,
    criticality: null,
    intrinsic_section_risk_score: null,
    intrinsic_section_risk_rag: null,
    section_criticality_mean: null,
    findingBuckets: { critical: 0, high: 0, medium: 0, low: 0, compliant: 0 },
  };
  if (!Array.isArray(scoredRows) || scoredRows.length === 0) return out;
  out.total = scoredRows.length;
  for (const r of scoredRows) {
    const tier = String(r?.auto_final_risk || "").toLowerCase();
    if (tier === "critical") out.findingBuckets.critical += 1;
    else if (tier === "high") out.findingBuckets.high += 1;
    else if (tier === "medium") out.findingBuckets.medium += 1;
    else if (tier === "low") out.findingBuckets.low += 1;
    if (String(r?.finding_type || "").toLowerCase() === "compliant") {
      out.findingBuckets.compliant += 1;
    }
  }

  // Pass 1: walk every row, gather QRS / Numeric / realized contributions.
  const qrss = [];
  let totalIntrinsic = 0;
  let totalRealized = 0;
  let totalNumeric = 0;
  let rubricRowCount = 0;

  for (const r of scoredRows) {
    const i = Number(r?.rubric_impact);
    const l = Number(r?.rubric_likelihood);
    const d = Number(r?.rubric_detectability);
    if (!Number.isFinite(i) || !Number.isFinite(l) || !Number.isFinite(d)) continue;

    out.scored += 1;
    const rs = i * l * d;
    qrss.push(rs);
    rubricRowCount += 1;

    const findingType = r?.finding_type;
    if (String(findingType || "").toLowerCase() === "compliant") out.compliant += 1;

    // 14030 AC #12 — exclude NotScored from BOTH numerator + denominator.
    if (isNotScored(findingType)) continue;
    totalIntrinsic += rs;
    if (isNonCompliant(findingType)) totalRealized += rs;

    const criticality = computeCriticality(rs, i, cfg);
    totalNumeric += lookupCriticalityNumeric(criticality, cfg);
  }

  // 14030 AC #12 — vendor-driven Risk Score %.
  if (totalIntrinsic > 0) {
    out.score = (1 - totalRealized / totalIntrinsic) * 100;
    out.rag = scoreToBand(out.score, cfg.vqq_rag_bands);
  } else if (out.scored > 0) {
    // All in-scope rows are Not Scored — vacuously compliant.
    out.score = 100;
    out.rag = scoreToBand(100, cfg.vqq_rag_bands);
  }

  // 14030 AC #7 — intrinsic Section Risk Score = Mean(QRS) + 0.5 × sample StDev.
  if (qrss.length > 0) {
    out.intrinsic_section_risk_score = meanPlusHalfStDev(qrss);
    out.intrinsic_section_risk_rag = scoreToBand(
      out.intrinsic_section_risk_score,
      cfg.section_risk_rag_bands || cfg.question_rag_bands,
    );
  }

  // 14030 AC #8a — Section Criticality from bucketed mean of Numeric weights.
  if (rubricRowCount > 0) {
    const mean = totalNumeric / rubricRowCount;
    out.section_criticality_mean = mean;
    out.criticality = classifyCriticalityMean(mean, cfg);
  }

  return out;
}

function meanPlusHalfStDev(values) {
  if (!values.length) return 0;
  if (values.length === 1) return values[0];
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const sumSq = values.reduce((acc, v) => acc + (v - mean) * (v - mean), 0);
  const sampleStDev = Math.sqrt(sumSq / (values.length - 1));
  const result = mean + 0.5 * sampleStDev;
  return Math.round(result * 10) / 10;
}

function lookupCriticalityNumeric(criticality, cfg) {
  if (!criticality) return 0;
  const map = cfg.question_criticality_numeric_map;
  if (!Array.isArray(map)) {
    return criticality === "Critical" ? 5 : criticality === "Major" ? 3 : 1;
  }
  const match = map.find(
    (m) => String(m.tier || "").toLowerCase() === String(criticality).toLowerCase(),
  );
  return match ? Number(match.weight) || 0 : 0;
}

function classifyCriticalityMean(mean, cfg) {
  const bands = cfg.section_criticality_bands;
  if (!Array.isArray(bands) || bands.length === 0) return "Minor";
  const sorted = [...bands].sort((a, b) => (a.priority ?? 99) - (b.priority ?? 99));
  for (const b of sorted) {
    const minOk = b.inclusive_min ? mean >= b.min_mean : mean > b.min_mean;
    const maxOk =
      b.max_mean == null
        ? true
        : b.inclusive_max
          ? mean <= b.max_mean
          : mean < b.max_mean;
    if (minOk && maxOk) return b.tier;
  }
  return "Minor";
}

/**
 * Map a 0-100 compliance score onto one of the config's VQQ RAG bands.
 * Inclusive min/max bounds; defaults to the last band when nothing matches.
 */
function scoreToBand(score, bands) {
  if (!Array.isArray(bands) || bands.length === 0) return null;
  for (const b of bands) {
    if (score >= b.min && score <= b.max) return b.band;
  }
  // Walking by descending min as a safety net (catches values above the top band's max).
  const ordered = [...bands].sort((a, b) => b.min - a.min);
  for (const b of ordered) {
    if (score >= b.min) return b.band;
  }
  return bands[bands.length - 1].band;
}

/**
 * 14030 — Section Risk Score badge label: prefer the rubric-derived intrinsic
 * 1-125 number (Mean+0.5*StDev) on its band. Backward-compat helper for callers
 * that still pass scoredRows; new callers should read intrinsic_section_risk_score
 * from aggregateSection directly.
 */
export function sectionScoreBand(scoredRows, config = null) {
  if (!Array.isArray(scoredRows) || scoredRows.length === 0) return null;
  const cfg = resolveConfig(config);
  const qrss = [];
  for (const r of scoredRows) {
    const i = Number(r?.rubric_impact);
    const l = Number(r?.rubric_likelihood);
    const d = Number(r?.rubric_detectability);
    if (Number.isFinite(i) && Number.isFinite(l) && Number.isFinite(d)) {
      qrss.push(i * l * d);
    }
  }
  if (qrss.length === 0) return null;
  const intrinsic = meanPlusHalfStDev(qrss);
  return scoreToBand(intrinsic, cfg.section_risk_rag_bands || cfg.question_rag_bands);
}

/**
 * Pick a color class for a regulatory standard out of the config-supplied
 * palette. Falls back to neutral gray when the config doesn't carry an entry
 * for a standard the question references.
 */
export function colorForStandard(standard, config = null) {
  const cfg = resolveConfig(config);
  const key = String(standard || "").trim().toUpperCase();
  const match = cfg.standard_palette.find(
    (e) => String(e.standard || "").trim().toUpperCase() === key
  );
  if (match) return `${match.bg} ${match.text} ${match.border}`.trim();
  return "bg-gray-50 text-gray-700 border-gray-200";
}

/**
 * What the Finding Severity filter sees for a single question row. Reads
 * the tier vocabulary from the config — change the bins, the filter
 * automatically shows the right tier labels.
 */
export function questionSeverityBucket(scoredRow, config = null) {
  if (!scoredRow) return "Not Scored";
  const finding = String(scoredRow.finding_type || "").toLowerCase();
  if (finding === "outofscope") return "Not Scored";
  if (finding === "compliant") return "Compliant";
  const cfg = resolveConfig(config);
  const tier = String(scoredRow.auto_final_risk || "");
  for (const bin of cfg.severity_tier_bins) {
    if (bin.tier.toLowerCase() === tier.toLowerCase()) return bin.tier;
  }
  return "Not Scored";
}

/**
 * Per-question criticality — same config-driven thresholds RiskPanelCard and
 * the section sidebar use.
 */
export function questionCriticality(scoredRow, config = null) {
  if (!scoredRow) return null;
  const i = Number(scoredRow.rubric_impact);
  const l = Number(scoredRow.rubric_likelihood);
  const d = Number(scoredRow.rubric_detectability);
  if (!Number.isFinite(i) || !Number.isFinite(l) || !Number.isFinite(d)) return null;
  return computeCriticality(i * l * d, i, config);
}

/**
 * For UI label generation — e.g. the filter chip "Red (≥ 50)" pulls the "50"
 * out of the active config's question_rag_bands.
 */
export function describeBand(band, bands) {
  if (!Array.isArray(bands)) return band;
  const match = bands.find((b) => b.band === band);
  if (!match) return band;
  if (match.min === 0) return `${band} (< ${match.max + 1})`;
  if (match.max >= 125 || match.max >= 100) return `${band} (≥ ${match.min})`;
  return `${band} (${match.min}–${match.max})`;
}
